import React from "react";
import moment from "moment";
import RemoteIcon from "./RemoteIcon";

const ReportTable = ({ reportData }) => {
  const type = reportData?.type;
  const dynamic = reportData?.dynamic;

  // Dynamic, code-based datasets from the report's `dynamic` block.
  const feelings = dynamic?.summary?.feelings || [];
  const feelingSeries = dynamic?.feelings_series || [];
  const negativeReasons = dynamic?.summary?.negative_reasons || [];
  const positiveReasons = dynamic?.summary?.positive_reasons || [];
  const reasonSeries = dynamic?.reasons_series || [];
  const polaritySplit = dynamic?.summary?.polarity_split || {};

  // Period header label + per-row label (period key is hour|date|month by group_by).
  const periodHeader = type === "daily" ? "Hour" : type === "monthly" ? "Date" : type === "weekly" ? "Weekly" : "Month";
  const periodLabel = (row) => {
    if (type === "daily") return moment(row.hour || row.date).format("MMM D YYYY h:mm A ");
    if (type === "monthly" || type === "weekly") return moment(row.date).format("MMM D YYYY");
    return row.month || row.date;
  };

  // Heat highlight: green = highest, yellow = 2nd, red = lowest (when > 2 values).
  const heatClass = (value, allValues) => {
    const vals = allValues.filter((v) => typeof v === "number" && !isNaN(v));
    if (!value) return "";
    const sorted = [...vals].sort((a, b) => b - a);
    if (value === sorted[0]) return "bg-[#88FA4E]";
    if (value === sorted[1] && sorted.length > 1) return "bg-[#FED477]";
    if (value === sorted[sorted.length - 1] && sorted.length > 2) return "bg-[#FF968D]";
    return "";
  };

  const feelingTotals = feelings.map((f) => f.count || 0);
  const reasonTotals = negativeReasons.map((r) => r.count || 0);
  const positiveReasonTotals = positiveReasons.map((r) => r.count || 0);
  const seriesCounts = feelingSeries.map((r) => r.Count || 0);
  const maxCount = seriesCounts.length ? Math.max(...seriesCounts) : 0;
  const minCount = seriesCounts.length ? Math.min(...seriesCounts) : 0;

  const hasData = feelings.length > 0;

  if (!hasData) {
    return (
      <div className="mb-6 text-center">
        <h2 className="text-xl font-semibold text-secondary-color">Well-being Analytical Report</h2>
        <p className="mt-8 text-sm text-grayColor300">No data available for the selected period.</p>
      </div>
    );
  }

  return (
    <>
      {/* Organization Name and Report Title */}
      <div className="mb-6 text-center">
        <h2 className="text-xl font-semibold text-secondary-color">Well-being Analytical Report</h2>
      </div>

      {/* Entries (feelings) table */}
      <div className="overflow-x-auto">
        <table className="mt-8 w-max min-w-full border-collapse overflow-hidden text-secondary-color">
          <thead className="bg-[#F6F7F8]">
            <tr>
              <th colSpan={feelings.length + 2} className="border border-primary-color-300 bg-white px-1 py-1 text-left text-lg">
                Entries
              </th>
            </tr>
            <tr className="text-base">
              <th className="border border-primary-color-300 px-1 py-1 text-left font-semibold min-w-[120px]">{periodHeader}</th>
              {feelings.map((feeling) => (
                <th key={feeling.code} className="border border-primary-color-300 px-1 py-1 text-left font-semibold min-w-[100px]">
                  <div className="flex items-center gap-x-2">
                    <RemoteIcon src={feeling.icon_data || feeling.icon_url} alt={feeling.name} className="h-7 w-7 object-contain" />
                    <span className="text-xs break-words">{feeling.name}</span>
                  </div>
                </th>
              ))}
              <th className="border border-primary-color-300 px-1 py-1 text-left font-semibold min-w-[80px]">Entries</th>
            </tr>
          </thead>
          <tbody>
            {feelingSeries.map((row, index) => (
              <tr key={index} className={`text-sm ${index % 2 === 0 ? "bg-white" : "bg-[#F6F7F8]"}`}>
                <td className="border border-primary-color-300 px-1 py-1">{periodLabel(row)}</td>
                {feelings.map((feeling) => (
                  <td key={feeling.code} className="border border-primary-color-300 px-1 py-1 text-right">
                    {row?.counts?.[feeling.code] ?? 0}
                  </td>
                ))}
                <td
                  className={`border border-primary-color-300 px-1 py-1 text-right ${row.Count === maxCount ? "bg-[#88FA4E]" : ""} ${
                    row.Count === minCount ? "bg-[#FF968D]" : ""
                  }`}
                >
                  {row.Count}
                </td>
              </tr>
            ))}

            {/* Totals */}
            <tr className="bg-white text-sm">
              <td className="border border-primary-color-300 px-1 py-1 font-semibold">Total</td>
              {feelings.map((feeling) => (
                <td
                  key={feeling.code}
                  className={`border border-primary-color-300 px-1 py-1 text-right font-semibold ${heatClass(feeling.count || 0, feelingTotals)}`}
                >
                  {feeling.count || 0}
                </td>
              ))}
              <td className="border border-primary-color-300 px-1 py-1"></td>
            </tr>

            {/* Percentages */}
            <tr className="bg-white text-sm font-bold">
              <td className="border border-primary-color-300 px-1 py-1"></td>
              {feelings.map((feeling) => (
                <td key={feeling.code} className="border border-primary-color-300 px-1 py-1 text-right">
                  {feeling.percentage ?? 0}%
                </td>
              ))}
              <td className="border border-primary-color-300 px-1 py-1"></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Settled / Unsettled split */}
      {(polaritySplit.healthy_percentage != null || polaritySplit.unhealthy_percentage != null) && (
        <div className="mt-4 flex w-full overflow-hidden rounded-md text-sm font-semibold">
          <div className="bg-[#88FA4E] px-3 py-1 text-center" style={{ width: `${polaritySplit.healthy_percentage || 0}%` }}>
            Settled {polaritySplit.healthy_percentage || 0}%
          </div>
          <div className="bg-[#FF968D] px-3 py-1 text-center" style={{ width: `${polaritySplit.unhealthy_percentage || 0}%` }}>
            Unsettled {polaritySplit.unhealthy_percentage || 0}%
          </div>
        </div>
      )}

      {/* Reasons table (negative reasons) */}
      <div className="overflow-x-auto">
        <table className="mt-12 w-max border-collapse overflow-hidden text-secondary-color">
          <thead className="bg-[#F6F7F8]">
            <tr>
              <th colSpan={negativeReasons.length + 1} className="border border-primary-color-300 bg-white px-1 py-1 text-left text-lg">
                Reasons ( meh, low, struggling )
              </th>
            </tr>
            <tr className="text-base">
              <th className="border border-primary-color-300 px-1 py-1 text-left font-semibold min-w-[120px]">{periodHeader}</th>
              {negativeReasons.map((reason) => (
                <th key={reason.code} className="border border-primary-color-300 px-1 py-1 text-left font-semibold min-w-[100px]">
                  <div className="flex items-center gap-x-2">
                    <RemoteIcon src={reason.icon_data || reason.icon_url} alt={reason.name} className="h-7 w-7 object-contain" />
                    <span className="text-xs break-words">{reason.name}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reasonSeries.map((row, index) => (
              <tr key={index} className={`text-sm ${index % 2 === 0 ? "bg-white" : "bg-[#F6F7F8]"}`}>
                <td className="border border-primary-color-300 px-1 py-1">{periodLabel(row)}</td>
                {negativeReasons.map((reason) => (
                  <td key={reason.code} className="border border-primary-color-300 px-1 py-1 text-right">
                    {row?.counts?.[reason.code] ?? 0}
                  </td>
                ))}
              </tr>
            ))}

            {/* Totals */}
            <tr className="bg-white text-sm">
              <td className="border border-primary-color-300 px-1 py-1 font-semibold">Total</td>
              {negativeReasons.map((reason) => (
                <td
                  key={reason.code}
                  className={`border border-primary-color-300 px-1 py-1 text-right font-semibold ${heatClass(reason.count || 0, reasonTotals)}`}
                >
                  {reason.count || 0}
                </td>
              ))}
            </tr>

            {/* Percentages */}
            <tr className="bg-white text-sm font-bold">
              <td className="border border-primary-color-300 px-1 py-1"></td>
              {negativeReasons.map((reason) => (
                <td key={reason.code} className="border border-primary-color-300 px-1 py-1 text-right">
                  {reason.percentage ?? 0}%
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Positive Reasons table (okay, good, great) */}
      {positiveReasons.length > 0 && (
        <div className="overflow-x-auto">
          <table className="mt-12 w-max border-collapse overflow-hidden text-secondary-color">
            <thead className="bg-[#F6F7F8]">
              <tr>
                <th colSpan={positiveReasons.length + 1} className="border border-primary-color-300 bg-white px-1 py-1 text-left text-lg">
                  Positive Reasons ( okay, good, great )
                </th>
              </tr>
              <tr className="text-base">
                <th className="border border-primary-color-300 px-1 py-1 text-left font-semibold min-w-[120px]">{periodHeader}</th>
                {positiveReasons.map((reason) => (
                  <th key={reason.code} className="border border-primary-color-300 px-1 py-1 text-left font-semibold min-w-[100px]">
                    <div className="flex items-center gap-x-2">
                      <RemoteIcon src={reason.icon_data || reason.icon_url} alt={reason.name} className="h-7 w-7 object-contain" />
                      <span className="text-xs break-words">{reason.name}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reasonSeries.map((row, index) => (
                <tr key={index} className={`text-sm ${index % 2 === 0 ? "bg-white" : "bg-[#F6F7F8]"}`}>
                  <td className="border border-primary-color-300 px-1 py-1">{periodLabel(row)}</td>
                  {positiveReasons.map((reason) => (
                    <td key={reason.code} className="border border-primary-color-300 px-1 py-1 text-right">
                      {row?.counts?.[reason.code] ?? 0}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Totals */}
              <tr className="bg-white text-sm">
                <td className="border border-primary-color-300 px-1 py-1 font-semibold">Total</td>
                {positiveReasons.map((reason) => (
                  <td
                    key={reason.code}
                    className={`border border-primary-color-300 px-1 py-1 text-right font-semibold ${heatClass(reason.count || 0, positiveReasonTotals)}`}
                  >
                    {reason.count || 0}
                  </td>
                ))}
              </tr>

              {/* Percentages */}
              <tr className="bg-white text-sm font-bold">
                <td className="border border-primary-color-300 px-1 py-1"></td>
                {positiveReasons.map((reason) => (
                  <td key={reason.code} className="border border-primary-color-300 px-1 py-1 text-right">
                    {reason.percentage ?? 0}%
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {reportData?.Evaluation?.length > 0 && (
        <div>
          <table className="mt-12 min-w-full border-collapse overflow-hidden text-secondary-color table-fixed">
            <thead className="bg-[#F6F7F8]">
              <tr>
                <th colSpan={3} className="border border-primary-color-300 bg-white px-1 py-1 text-left text-lg">
                  Evaluation Data
                </th>
              </tr>
              <tr className="text-base">
                <th className="w-1/5 border border-primary-color-300 px-1 py-1 text-left font-semibold">Date</th>
                <th className="w-2/5 border border-primary-color-300 px-1 py-1 text-left font-semibold">Evaluation of data</th>
                <th className="w-2/5 border border-primary-color-300 px-1 py-1 text-left font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reportData?.Evaluation?.map((row, index) => (
                <tr key={index} className={`text-sm${index % 2 === 0 ? " bg-white" : " bg-[#F6F7F8]"}`}>
                  <td className="border border-primary-color-300 px-1 py-1 text-start align-top">
                    {moment.unix(row?.created_at_unix).format("D MMM YYYY")}
                  </td>
                  <td className="border border-primary-color-300 px-1 py-1 text-start" dangerouslySetInnerHTML={{ __html: row?.evaluation }} />
                  <td className="border border-primary-color-300 px-1 py-1 text-start align-top" dangerouslySetInnerHTML={{ __html: row?.action }} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default ReportTable;
